// Consolidated `/admin/trust` page — three semantically-related
// trust mechanics live under one tabbed shell so the operator who
// wants to set up "who can sign artifacts here" finds all three on
// one page (item 91 Phase A of PRODUCT-ROADMAP.md):
//
//   * X.509 / CMS / OpenPGP / RSA signing keys (was `/admin/trust`)
//   * Sigstore / cosign trust policies          (was `/admin/sigstore`)
//   * OIDC trust policies                       (was `/admin/oidc/policies`)
//
// The active tab is driven by URL fragment (`/admin/trust#sigstore`,
// `/admin/trust#oidc`). The legacy URLs `<Navigate replace />` to the
// matching anchor in App.tsx so existing bookmarks keep working.

import { ShieldCheck, BadgeCheck, KeyRound } from 'lucide-react';
import { AdminShell } from '../../components/AdminShell';
import { Tabs, type TabItem } from '../../components/ui/Tabs';
import { AdminTrustX509Tab } from './AdminTrustX509Tab';
import { AdminTrustSigstoreTab } from './AdminTrustSigstoreTab';
import { AdminTrustOidcTab } from './AdminTrustOidcTab';

const TABS: TabItem[] = [
  {
    id: 'x509',
    label: (
      <span className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4" />
        X.509 / OpenPGP
      </span>
    ),
    panel: <AdminTrustX509Tab />,
  },
  {
    id: 'sigstore',
    label: (
      <span className="flex items-center gap-2">
        <BadgeCheck className="h-4 w-4" />
        Sigstore
      </span>
    ),
    panel: <AdminTrustSigstoreTab />,
  },
  {
    id: 'oidc',
    label: (
      <span className="flex items-center gap-2">
        <KeyRound className="h-4 w-4" />
        OIDC
      </span>
    ),
    panel: <AdminTrustOidcTab />,
  },
];

export function AdminTrust() {
  return (
    <AdminShell
      title={
        <span className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-nebula" />
          Trust
        </span>
      }
      description="One surface for the three trust mechanics that govern who can sign artifacts: X.509 / OpenPGP signing keys for verify-on-pull, Sigstore (cosign) identity policies for keyless CI signatures, and OIDC trust policies for short-lived service-account tokens."
    >
      <Tabs items={TABS} defaultId="x509" ariaLabel="Trust mechanics" />
    </AdminShell>
  );
}
